/**
 * React Query hooks for CRM custom field definitions.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, {
  CrmCreateCustomFieldInput,
  CrmCustomFieldDefinition,
  CrmCustomFieldEntityType,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function useCustomFields(entityType?: CrmCustomFieldEntityType) {
  const queryClient = useQueryClient();

  const query = useQuery<{ fields: CrmCustomFieldDefinition[] }>({
    queryKey: queryKeys.crm.customFields.list(entityType),
    queryFn: () => api.listCrmCustomFields(entityType),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.crm.customFields.all() });

  const createMutation = useMutation({
    mutationFn: (input: CrmCreateCustomFieldInput) => api.createCrmCustomField(input),
    onSuccess: invalidate,
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to create custom field',
        variant: 'destructive',
      }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CrmCreateCustomFieldInput> }) =>
      api.updateCrmCustomField(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to update custom field',
        variant: 'destructive',
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCrmCustomField(id),
    onSuccess: invalidate,
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to delete custom field',
        variant: 'destructive',
      }),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load custom fields',
      variant: 'destructive',
    });
  }

  return {
    fields: query.data?.fields ?? [],
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createField: (input: CrmCreateCustomFieldInput) => createMutation.mutateAsync(input),
    updateField: (id: string, input: Partial<CrmCreateCustomFieldInput>) =>
      updateMutation.mutateAsync({ id, input }),
    deleteField: (id: string) => deleteMutation.mutateAsync(id),
  };
}
